import { useContext, useState, useEffect } from 'react'
import { fetchAPI } from '@/api/client'
import { DashboardContext } from '@/context/DashboardContext'
import type { OverflowWindowInfo } from '@/types/api'
import { computeOverflowWindowClient } from '@/utils/timer'

export interface OverflowWindowState {
  window: OverflowWindowInfo | null
  loading: boolean
  error: string | null
}

/**
 * Server-authoritative overflow priority window — mirrors cap-api /api/cap/overflow-window.
 *
 * `offerId` scopes it to that offer's reset zone; without it the endpoint answers for the
 * UTC day, which would be the wrong countdown on a Pacific offer.
 */
export function useOverflowWindow(pollMs = 60_000, enabled = true, offerId?: number | null): OverflowWindowState {
  const [state, setState] = useState<OverflowWindowState>({ window: null, loading: true, error: null })

  useEffect(() => {
    if (!enabled) {
      setState({ window: null, loading: false, error: null })
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        const w = await fetchAPI<OverflowWindowInfo>(
          offerId ? `/api/cap/overflow-window?offer=${offerId}` : '/api/cap/overflow-window'
        )
        if (!cancelled) setState({ window: w, loading: false, error: null })
      } catch (err) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load overflow window',
          }))
        }
      }
    }
    load()
    const id = setInterval(load, pollMs)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [pollMs, enabled, offerId])

  return state
}

/**
 * Resolved window — API when loaded, client-computed fallback otherwise.
 *
 * Prefers the window already in the dashboard payload (it is per offer and arrives with
 * the rest of the data), then its own poll, then the client fallback seeded with whatever
 * reset instant is known. Outside `DashboardProvider` there is no selected offer, so it
 * falls through to the UTC day.
 */
export function useResolvedOverflowWindow(enabled = true): OverflowWindowInfo {
  const ctx = useContext(DashboardContext)
  const fromDashboard = ctx?.overflowWindow ?? null
  const { window } = useOverflowWindow(60_000, enabled && !fromDashboard, ctx?.selectedOfferId ?? null)
  const resolved = fromDashboard ?? window
  return resolved ?? computeOverflowWindowClient(new Date(), null)
}
